import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Phone, Clock } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import StickyCallButton from '@/components/StickyCallButton';
import PageSEO from '@/components/PageSEO';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlzPage = () => {
  const { plz } = useParams();
  const [plzData, setPlzData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlz = async () => {
      try {
        const response = await axios.get(`${API}/plz/${plz}`);
        setPlzData(response.data);
      } catch (error) {
        console.error('Fehler beim Laden der PLZ-Daten:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlz();
  }, [plz]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-500">Lade Daten...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!plzData) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex flex-col items-center justify-center py-20">
          <p className="text-gray-500 text-lg mb-4">Postleitzahl nicht gefunden</p>
          <Link to="/einsatzgebiete" className="text-[#0066cc] hover:underline">Zurück zur Übersicht</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <PageSEO
        title={`Handwerkernotdienst PLZ ${plz} ${plzData.city ? `- ${plzData.city}` : ''} | 24/7 Notdienst`}
        description={`24/7 Handwerkernotdienst für PLZ ${plz}${plzData.city ? ` in ${plzData.city}` : ''}. Schlüsseldienst, Elektro, Sanitär, Rohrreinigung & mehr. Schnell vor Ort ☎ 0231 39083687.`}
        canonicalUrl={`https://handwerkernotdienst.nrw/plz/${plz}`}
        breadcrumbs={[
          { name: 'Start', url: 'https://handwerkernotdienst.nrw/' },
          { name: 'Einsatzgebiete', url: 'https://handwerkernotdienst.nrw/einsatzgebiete' },
          { name: `PLZ ${plz}`, url: `https://handwerkernotdienst.nrw/plz/${plz}` }
        ]}
      />
      
      <Header />
      <StickyCallButton />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#e8f4f8] to-white py-16" data-testid="plz-hero">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-[#0066cc]">Start</Link>
            <span>/</span>
            <Link to="/einsatzgebiete" className="hover:text-[#0066cc]">Einsatzgebiete</Link>
            <span>/</span>
            <span className="text-gray-900">PLZ {plz}</span>
          </div>

          <div className="flex items-start space-x-4 mb-6">
            <div className="w-16 h-16 bg-[#0066cc] rounded-xl flex items-center justify-center flex-shrink-0">
              <MapPin className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3" data-testid="plz-title">
                Notdienst für PLZ {plz}
              </h1>
              <p className="text-lg text-gray-600">
                24/7 Handwerkernotdienst in {plzData.city}
              </p>
            </div>
          </div>

          <a href="tel:+4923139083687" data-testid="plz-call-button">
            <Button size="lg" className="bg-[#e63946] hover:bg-[#d62828] text-white px-8 rounded-full font-bold">
              <Phone className="w-5 h-5 mr-2" />
              Jetzt anrufen: 0231 39083687
            </Button>
          </a>
        </div>
      </section>

      {/* Content */}
      <section className="py-16" data-testid="plz-content">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Handwerkernotdienst im Postleitzahlenbereich {plz}
            </h2>
            
            <p className="text-gray-700 leading-relaxed mb-6">
              Die Postleitzahl {plz} gehört zu {plzData.city} in Nordrhein-Westfalen. 
              Unser Handwerkernotdienst ist in diesem gesamten Postleitzahlenbereich für Sie im Einsatz – 
              schnell, zuverlässig und rund um die Uhr.
            </p>

            <p className="text-gray-700 leading-relaxed mb-6">
              Egal ob Schlüsseldienst, Elektro-Notdienst, Sanitär-Notfall oder Glas-Notdienst – 
              wir kommen schnellstmöglich zu Ihnen in den Bereich {plz}. Mit unserer Erfahrung und 
              professionellen Ausrüstung lösen wir Ihr Problem effizient und fachgerecht.
            </p>

            <div className="grid md:grid-cols-3 gap-6 my-8">
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <Clock className="w-10 h-10 text-[#0066cc] mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">24/7 Verfügbar</h3>
                <p className="text-gray-600 text-sm">Auch nachts und am Wochenende</p>
              </div>
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <MapPin className="w-10 h-10 text-[#0066cc] mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Schnell vor Ort</h3>
                <p className="text-gray-600 text-sm">Kurze Anfahrtszeiten in PLZ {plz}</p>
              </div>
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <Phone className="w-10 h-10 text-[#0066cc] mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900 mb-2">Kostenlose Beratung</h3>
                <p className="text-gray-600 text-sm">Transparente Abläufe</p>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-4">Unsere Leistungen in PLZ {plz}</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li>Schlüsseldienst und Türöffnung ohne Beschädigung</li>
              <li>Elektro-Notdienst bei Stromausfall und elektrischen Notfällen</li>
              <li>Sanitär-Notdienst bei Rohrbruch, Wasserschaden und Verstopfungen</li>
              <li>Heizungsnotdienst bei Heizungsausfall</li>
              <li>Rohrreinigung professionell und schnell</li>
              <li>Glas-Notdienst und Notverglasung</li>
              <li>Einbruchschutz und Sicherheitstechnik</li>
            </ul>

            {plzData.districts && plzData.districts.length > 0 && (
              <div className="bg-[#e8f4f8] p-6 rounded-xl my-8">
                <h4 className="font-semibold text-gray-900 mb-3">
                  Stadtteile im Bereich PLZ {plz}
                </h4>
                <p className="text-gray-700 mb-3">
                  Die Postleitzahl {plz} umfasst folgende Stadtteile:
                </p>
                <div className="flex flex-wrap gap-3">
                  {plzData.districts.map((district, index) => (
                    <Link
                      key={index}
                      to={`/einsatzgebiete/${plzData.citySlug}/${district.slug}`}
                      className="bg-white px-4 py-2 rounded-lg text-[#0066cc] font-semibold hover:bg-[#0066cc] hover:text-white transition-colors"
                      data-testid={`district-link-${index}`}
                    >
                      {district.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">So erreichen Sie uns</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Bei einem Notfall in PLZ {plz} zögern Sie nicht, uns sofort anzurufen. 
              Unsere freundlichen Mitarbeiter nehmen Ihren Anruf entgegen, beraten Sie kostenlos 
              und schicken schnellstmöglich einen qualifizierten Techniker zu Ihnen.
            </p>
            <div className="bg-white border-2 border-[#0066cc] p-6 rounded-xl my-8">
              <h4 className="font-semibold text-gray-900 mb-3 text-lg">Kontaktdaten</h4>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Telefon (24/7):</strong> <a href="tel:+4923139083687" className="text-[#0066cc] font-semibold hover:underline">0231 39083687</a></li>
                <li><strong>E-Mail:</strong> <a href="mailto:info@handwerkernotdienst.nrw" className="text-[#0066cc] hover:underline">info@handwerkernotdienst.nrw</a></li>
                <li><strong>Einsatzgebiet:</strong> {plzData.city} (PLZ {plz}) und Umgebung</li>
              </ul>
            </div>

            <div className="mt-8">
              <Link
                to={`/einsatzgebiete/${plzData.citySlug}`}
                className="text-[#0066cc] hover:underline font-semibold"
                data-testid="back-to-city-link"
              >
                ← Zurück zu {plzData.city}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PlzPage;